/**
 * LedgerManager (formerly ListManagement)
 * Shows all sessions as "Ledger Cards" sorted by date — clicking opens the ledger directly.
 * Active sessions first, then upcoming, then completed.
 */
import { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { auth } from '../lib/firebase';
import { subscribeGames } from '../lib/gameService';
import { getCompletedGames } from '../lib/gameService';
import { TopNav } from '../components/TopNav';
import { useNavigate } from 'react-router-dom';
import { Layers, ChevronRight, Calendar, Clock, Zap, PlayCircle, CalendarClock, Users } from 'lucide-react';
import clsx from 'clsx';

const pad = (n) => String(n).padStart(2, '0');

function useStopwatch(isoStart) {
    const calc = useCallback(() => {
        if (!isoStart) return { h: 0, m: 0, s: 0 };
        const elapsed = Math.max(0, Date.now() - new Date(isoStart));
        const totalSec = Math.floor(elapsed / 1000);
        return { h: Math.floor(totalSec / 3600), m: Math.floor((totalSec % 3600) / 60), s: totalSec % 60 };
    }, [isoStart]);

    const [parts, setParts] = useState(calc);
    useEffect(() => {
        if (!isoStart) return;
        setParts(calc());
        const id = setInterval(() => setParts(calc()), 1000);
        return () => clearInterval(id);
    }, [calc, isoStart]);
    return parts;
}

function useCountdown(isoTarget) {
    const calc = useCallback(() => {
        const diff = Math.max(0, new Date(isoTarget) - Date.now());
        const s = Math.floor(diff / 1000);
        return { d: Math.floor(s / 86400), h: Math.floor((s % 86400) / 3600), m: Math.floor((s % 3600) / 60), s: s % 60 };
    }, [isoTarget]);

    const [parts, setParts] = useState(calc);
    useEffect(() => {
        setParts(calc());
        const id = setInterval(() => setParts(calc()), 1000);
        return () => clearInterval(id);
    }, [calc]);
    return parts;
}

function ActiveLedgerStrip({ session }) {
    const { h, m, s } = useStopwatch(session.startedAt || session.startTime);
    return (
        <div className="w-[4.5rem] flex-shrink-0 bg-emerald-500/10 border-r-[3px] border-r-emerald-500 flex flex-col items-center justify-center py-4 px-1 gap-1 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-b from-emerald-500/10 via-transparent to-transparent" />
            <div className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse shadow-[0_0_12px_rgba(52,211,153,0.8)]" />
            <span className="text-[12px] tracking-tight font-black text-emerald-400 tabular-nums leading-none mt-1">
                {pad(h)}:{pad(m)}
            </span>
            <span className="text-[8px] font-black text-emerald-300/80 tracking-[0.2em] uppercase mt-1">LIVE</span>
        </div>
    );
}

function UpcomingLedgerStrip({ session }) {
    const { d, h, m } = useCountdown(session.startTime);
    return (
        <div className="w-[4.5rem] flex-shrink-0 bg-amber-500/10 border-r-[3px] border-r-amber-500 flex flex-col items-center justify-center py-4 px-1 gap-1 relative overflow-hidden">
            <CalendarClock className="w-5 h-5 text-amber-500/60" />
            <span className="text-[12px] tracking-tight font-black text-amber-400 tabular-nums leading-none mt-1">
                {d > 0 ? `${d}d` : `${pad(h)}:${pad(m)}`}
            </span>
            <span className="text-[8px] font-black text-slate-500 tracking-[0.1em] mt-1 uppercase">SOON</span>
        </div>
    );
}

function CompletedLedgerStrip({ session }) {
    const net = session.netBalance || 0;
    return (
        <div className="w-[4.5rem] flex-shrink-0 bg-slate-800/80 border-r-[3px] border-r-slate-600 flex flex-col items-center justify-center py-4 px-1 gap-1 relative overflow-hidden">
            <span className="text-[8px] font-black text-slate-500 tracking-wider">POT</span>
            <span className={clsx(
                'text-[13px] font-black tabular-nums leading-none',
                net >= 0 ? 'text-emerald-400' : 'text-rose-400'
            )}>
                ₪{Math.abs(net).toLocaleString()}
            </span>
            <span className="text-[8px] font-black text-slate-600 tracking-[0.2em] mt-1 uppercase">DONE</span>
        </div>
    );
}

function LedgerCard({ session, onClick }) {
    const { language } = useLanguage();
    const isMultiTable = session.capacity === '2 Tables';

    const dateStr = session.startTime
        ? new Date(session.startTime).toLocaleDateString(
            language === 'he' ? 'he-IL' : 'en-US',
            { weekday: 'short', day: 'numeric', month: 'short' }
          )
        : '—';
    const timeStr = session.startTime
        ? new Date(session.startTime).toLocaleTimeString(
            language === 'he' ? 'he-IL' : 'en-US',
            { hour: '2-digit', minute: '2-digit' }
          )
        : '';

    const variantStr = isMultiTable
        ? `${session.table1Config?.variant?.includes('PLO') ? 'PLO' : 'NLH'} / ${session.table2Config?.variant?.includes('PLO') ? 'PLO' : 'NLH'}`
        : session.variant?.includes('PLO') ? 'PLO' : 'NLH';
    const stakesStr = isMultiTable
        ? `${session.table1Config?.stakes || '?'} & ${session.table2Config?.stakes || '?'}`
        : session.stakes;

    const isActive = session.status === 'ongoing';

    return (
        <button
            onClick={onClick}
            className={clsx(
                'w-full flex items-stretch rounded-3xl overflow-hidden transition-all active:scale-[0.98] text-right shadow-xl group',
                isActive ? 'bg-slate-900 border border-emerald-500/20 shadow-emerald-500/5' : 'bg-slate-900 border border-white/5',
                session.status === 'completed' && 'opacity-80 grayscale-[0.2]'
            )}
        >
            {/* ── Content ── */}
            <div className="flex-1 min-w-0 p-6 flex flex-col justify-center gap-1">
                <div className="flex items-center gap-3">
                    <span className="text-2xl font-black text-white font-heebo tracking-tighter uppercase">{dateStr}</span>
                    <span className="text-sm font-bold text-slate-500 tabular-nums">{timeStr}</span>
                    <span className={clsx(
                        'text-[9px] font-black px-2.5 py-1 rounded-lg border uppercase tracking-widest mr-auto shadow-lg',
                        isMultiTable 
                            ? 'bg-amber-500 border-amber-400 text-slate-950 shadow-amber-500/20' 
                            : 'bg-slate-800 border-white/5 text-slate-400'
                    )}>
                        {isMultiTable ? 'MASTER' : 'SINGLE'}
                    </span>
                </div>
                <div className="flex items-center gap-3 text-sm text-slate-400 truncate font-heebo mt-2 uppercase font-black tracking-widest">
                    <span className="text-indigo-400/90">{variantStr}</span>
                    <span className="text-slate-800">·</span>
                    <span className="text-amber-400/90">{stakesStr}</span>
                </div>
            </div>

            {/* ── Nerve Center Strip (Far Left in RTL) ── */}
            {session.status === 'ongoing' && <ActiveLedgerStrip session={session} />}
            {session.status === 'upcoming' && <UpcomingLedgerStrip session={session} />}
            {session.status === 'completed' && <CompletedLedgerStrip session={session} />}
        </button>
    );
}

export const ListManagement = () => {
    const { t } = useLanguage();
    const navigate = useNavigate();
    const hostId = auth.currentUser?.uid;

    const [activeSessions, setActiveSessions]     = useState([]);
    const [completedSessions, setCompletedSessions] = useState([]);
    const [loading, setLoading] = useState(true);

    // Subscribe to ongoing + upcoming
    useEffect(() => {
        if (!hostId) return;
        const unsub = subscribeGames(hostId, (games) => {
            // Sort: ongoing first, then upcoming by startTime asc
            const sorted = [...games].sort((a, b) => {
                if (a.status === 'ongoing' && b.status !== 'ongoing') return -1;
                if (b.status === 'ongoing' && a.status !== 'ongoing') return  1;
                return new Date(a.startTime) - new Date(b.startTime);
            });
            setActiveSessions(sorted);
            setLoading(false);
        });
        return unsub;
    }, [hostId]);

    // Fetch completed
    useEffect(() => {
        if (!hostId) return;
        getCompletedGames(hostId, 20)
            .then(setCompletedSessions)
            .catch(console.error);
    }, [hostId, activeSessions]);

    const allSessions = [
        ...activeSessions,
        ...completedSessions,
    ];

    const goToLedger = (session) => {
        // Multi-table → Portal, Single-table → direct ledger
        if (session.capacity === '2 Tables') {
            navigate(`/session/${session.id}/portal`);
        } else {
            navigate(`/session/${session.id}/ledger`);
        }
    };

    return (
        <div className="flex flex-col min-h-[100dvh] bg-slate-950 text-white font-outfit" dir="rtl">
            <TopNav />

            <div className="flex flex-col flex-1 min-h-0 w-full max-w-xl mx-auto px-4 py-6">
                {/* Header */}
                <div className="mb-4 flex-shrink-0">
                    <div className="flex items-center gap-2">
                        <Layers className="w-5 h-5 text-amber-500" />
                        <h1 className="text-[clamp(1.4rem,4vw,1.8rem)] font-black text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600 leading-none font-heebo">
                            {t('navActiveGame')}
                        </h1>
                    </div>
                    <p className="text-xs text-slate-500 mt-1 font-heebo">{t('ledgerManagerSubtitle')}</p>
                </div>

                {/* Scrollable ledger list */}
                <div className="flex-1 overflow-y-auto min-h-0 space-y-2 pb-4">
                    {loading ? (
                        [1, 2, 3].map(i => <div key={i} className="h-16 bg-slate-900/50 rounded-2xl animate-pulse" />)
                    ) : allSessions.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-16 text-slate-600">
                            <Layers className="w-10 h-10 mb-3 opacity-30" />
                            <p className="text-sm font-heebo">{t('noUpcomingSessions')}</p>
                        </div>
                    ) : allSessions.map(s => (
                        <LedgerCard
                            key={s.id}
                            session={s}
                            onClick={() => goToLedger(s)}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};

